import type { Theme } from '@mui/material/styles';
import type { SvgPaints } from './tokens';
import { colors } from './tokens/primitives';

type ColorKey = keyof typeof colors;

const PAINTS = ['fill', 'stroke'] as const;

// SVG assets are drawn in these Elementary neutrals.
export const SVG_NEUTRAL_KEYS = (Object.keys(colors) as ColorKey[]).filter(
  (key) => key === 'black' || key === 'slate' || /^gray\d*$/.test(key),
);
const NEUTRAL_VALUES = SVG_NEUTRAL_KEYS.map((key) => colors[key]);
const WHITE_VALUES = ['white', '#fff', colors.white];
const BLACK_VALUES = ['black', '#000', '#000000'];

const withPaint = (paint: string, values: string[]) =>
  values.map((value) => `[${paint}="${value}" i]`).join(', ');

const withAnyPaint = (values: string[]) =>
  PAINTS.map((paint) => withPaint(paint, values)).join(', ');

// Icons drawn with the Elementary neutrals. Brand logos never use them, so
// their white parts are left alone.
const NEUTRAL_ICON = `svg:has(${withAnyPaint(NEUTRAL_VALUES)})`;

// Icons drawn only in black and white, such as monochrome logos.
const MONOCHROME_ICON = `svg:has(${withAnyPaint(
  BLACK_VALUES,
)}):not(:has(${PAINTS.map(
  (paint) =>
    `[${paint}]:not(${withPaint(paint, [
      'none',
      'currentColor',
      ...WHITE_VALUES,
      ...BLACK_VALUES,
    ])})`,
).join(', ')}))`;

const ICON_PARTS = [NEUTRAL_ICON, MONOCHROME_ICON]
  .flatMap((icon) => [icon, `${icon} *`])
  .join(', ');

const ANY_NEUTRAL_VALUES = [
  'none',
  'currentColor',
  ...NEUTRAL_VALUES,
  ...WHITE_VALUES,
  ...BLACK_VALUES,
];

// Icons drawn only in neutrals, black and white, plus any paints the look
// repaints. Any other paint marks a colored icon or a logo.
const neutralOnlyIcon = (extraNeutrals: string[]) =>
  `svg:not(:has(${PAINTS.map(
    (paint) =>
      `[${paint}]:not(${withPaint(paint, [
        ...ANY_NEUTRAL_VALUES,
        ...extraNeutrals,
      ])})`,
  ).join(', ')}))`;

// Mask luminance decides what shows, so mask colors must stay as drawn.
const toRule = (paint: string, selector: string, color: string) =>
  `:where(${selector}:not(mask *)) { ${paint}: ${color}; }`;

// Paints neutral-only icons inside `scope` in its text color, counting the
// paints the look repaints as neutrals. The rules add no specificity beyond
// `scope`, so component styles and inline tints win.
export const getTextColoredIconRules = (scope: string, theme: Theme) => {
  const icon = neutralOnlyIcon(Object.keys(theme.tokens.svgPaints.paints));
  return PAINTS.map(
    (paint) =>
      `${scope} :where(:is(${icon}, ${icon} *)[${paint}]:not(${withPaint(
        paint,
        ['none'],
      )}, mask *)) { ${paint}: currentColor; }`,
  ).join('\n');
};

// Dark ink of brand wordmarks (GitHub, GitLab, Bitbucket). On dark surfaces
// the brands use reversed wordmarks, so the ink takes `brandInk` and the
// colored marks stay as drawn.
const BRAND_INK_VALUES = ['#24292E', '#171321', '#253858'];

export const getBrandInkRules = (brandInk?: string) =>
  brandInk
    ? PAINTS.map((paint) =>
        toRule(paint, `:is(${withPaint(paint, BRAND_INK_VALUES)})`, brandInk),
      ).join('\n')
    : '';

// These zero-specificity rules repaint the colors SVG assets are drawn in
// with the look's colors, so component styles still win. A look that keeps
// the Elementary paints gets no rules.
export const getIconColorRules = ({ paints, white, black }: SvgPaints) =>
  PAINTS.flatMap((paint) => [
    ...Object.entries(paints)
      .filter(([value, color]) => color !== value)
      .map(([value, color]) => toRule(paint, `[${paint}="${value}" i]`, color)),
    ...(white
      ? [
          toRule(
            paint,
            `:is(${ICON_PARTS}):is(${withPaint(paint, WHITE_VALUES)})`,
            white,
          ),
        ]
      : []),
    ...(black
      ? [
          toRule(
            paint,
            `:is(${ICON_PARTS}):is(${withPaint(paint, BLACK_VALUES)})`,
            black,
          ),
        ]
      : []),
  ]).join('\n');

// The gray disc behind the AI avatar.
export const AVATAR_DISC_SELECTOR = 'svg rect[fill="#CDCCCC" i]';

// The paint of the panel icon on the AI avatar's disc while the AI panel is
// open.
export const AVATAR_ICON_PAINT = colors.black;

// Pink line illustrations on white tiles (integration cards), redrawn as
// `line` art on a `tile`. A look without a tile color keeps them as drawn.
const ILLUSTRATION_LINE_VALUES = ['#FF3A6A', '#F2386C'];

export const getIllustrationRules = ({
  tile,
  line,
  radius,
}: {
  tile: string;
  line: string;
  radius: string;
}) => {
  if (!tile) return '';
  const linePaints = withAnyPaint(ILLUSTRATION_LINE_VALUES);
  const scope = `svg:has(${linePaints})`;
  // Some illustrations have no tile of their own.
  return `
    &:has(${linePaints}) {
      background: ${tile};
      border-radius: ${radius};
    }
    ${scope} :is(${withPaint('fill', WHITE_VALUES)}) {
      fill: ${tile};
    }
    ${scope} :is(${withPaint('fill', ILLUSTRATION_LINE_VALUES)}) {
      fill: ${line};
    }
    ${scope} :is(${withPaint('stroke', ILLUSTRATION_LINE_VALUES)}) {
      stroke: ${line};
    }
  `;
};

// The dark ink of the Elementary wordmark, for headers on a brand bar.
export const WORDMARK_INK_SELECTOR = 'svg path[fill="#1E2028" i]';
